import { Card, Flex, Typography } from '@iheartjane/reefer';
import type {
  Nullish,
  ProductCardCtaIcon,
  ProductCardCtaStyle,
  ProductCardHover,
  ProductCardSize,
} from '@iheartjane/types';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import { forwardRef, useCallback, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

import { JaneGoldTag } from '../janeGoldTag/janeGoldTag';
import { SpecialTag } from '../specialTag/specialTag';
import { CardMenu } from './cardMenu/cardMenu';
import type { ProductCardMenuItem } from './cardMenu/cardMenu';
import { CardText } from './cardText';
import { CtaButton } from './ctaButton/ctaButton';

import styles from './productCard.module.css';

interface ProductLinkProps {
  'aria-hidden'?: boolean;
  children: ReactNode;
  className?: string;
  onClick?: () => void;
  tabIndex?: number;
}

export interface ProductCardProps {
  atMaxQuantity?: boolean;
  /** Brand name and subtype */
  brandInfo?: Nullish<string>;
  cardBorder?: boolean;
  /** Card's overflow menu. */
  cardMenuItems?: ProductCardMenuItem[];
  cardSize?: ProductCardSize;
  cartCount?: number;
  className?: string;
  /** Ignored for card_bottom_full ctaStyle. */
  ctaIcon?: ProductCardCtaIcon;
  ctaStyle?: ProductCardCtaStyle;
  /** Dosage/pack info (e.g. ".58g / 1.75g per pack") */
  dosageInfo?: Nullish<string>;
  /** Load the product image eagerly rather than lazily (above-the-fold cards). */
  eagerLoad?: boolean;
  goldTagLabel?: Nullish<string>;
  /** Shortens product name to leave room for multi-weight pricing. */
  hasMultipleWeightDiscount?: boolean;
  hasMultipleWeights?: boolean;
  /** Suppress the strikethrough original price (state-based compliance rule). */
  hideStrikethrough?: boolean;
  hover?: ProductCardHover;
  /** Product page URL for the default link renderer; supply `renderLink` if absent. */
  href?: string;
  imageAlt?: string;
  imageSizes?: string;
  imageSrcSet?: string;
  imageUrl?: string;
  isDiscounted?: boolean;
  /** Renders mid-grid placement card variant (white background, padding). */
  isPlacement?: boolean;
  isSponsored?: boolean;
  isUpdatingCart?: boolean;
  name: string;
  /** Single-weight add-to-bag handler; ignored when `renderAddToBag` is provided. */
  onAddToBag?: () => void;
  onDecrease?: () => void;
  onImpress?: () => void;
  onIncrease?: () => void;
  onProductClick?: () => void;
  onSelectWeight?: () => void;
  originalPrice?: Nullish<string>;
  /** Lab results / potency summary (e.g. "THC 24.67% CBD 0.16%") */
  potencyInfo?: Nullish<string>;
  price?: Nullish<string>;
  /** Rating summary (out of 5 stars) and review count (e.g. "4.9 (1.2k)") */
  ratingSummary?: Nullish<string>;
  /** Escape hatch for SSR form submission. */
  renderAddToBag?: (button: ReactNode) => ReactNode;
  /** Render prop for the product link wrapping the image/text; defaults to a plain `<a href>`. */
  renderLink?: (props: ProductLinkProps) => ReactNode;
  /** Render prop for the category label (lineage + kind); app-specific presentation. */
  renderProductCategoryLabel: (args: { className: string }) => ReactNode;
  /** Renders a button over the top corner of the product image */
  renderImageAction?: (args: {
    cardSize: ProductCardSize;
    /** Moves focus to the card's menu button.  */
    focusCardMenu: () => void;
  }) => ReactNode;
  specialLabel?: Nullish<string>;
}

export const ProductCard = forwardRef<HTMLDivElement, ProductCardProps>(
  function ProductCard(
    {
      atMaxQuantity,
      brandInfo,
      cardBorder,
      cardMenuItems,
      cardSize = 'medium',
      cartCount,
      className,
      ctaIcon = 'add',
      ctaStyle = 'card_bottom_full',
      dosageInfo,
      hover = 'shadow',
      eagerLoad,
      goldTagLabel,
      hasMultipleWeights,
      hasMultipleWeightDiscount,
      hideStrikethrough,
      href,
      imageAlt,
      imageSizes,
      imageSrcSet,
      imageUrl,
      isDiscounted,
      isPlacement,
      isSponsored,
      isUpdatingCart,
      name,
      onAddToBag,
      onDecrease,
      onImpress,
      onIncrease,
      onProductClick,
      onSelectWeight,
      originalPrice,
      potencyInfo,
      price,
      ratingSummary,
      renderAddToBag,
      renderLink,
      renderProductCategoryLabel,
      renderImageAction,
      specialLabel,
    },
    ref
  ) {
    const onImpressRef = useRef(onImpress);
    useEffect(() => {
      onImpressRef.current = onImpress;
    }, [onImpress]);

    const { ref: impressionRef } = useInView({
      onChange: useCallback((inView: boolean) => {
        if (inView) onImpressRef.current?.();
      }, []),
      threshold: 0.5,
      triggerOnce: true,
    });

    const defaultRenderLink = ({
      'aria-hidden': ariaHidden,
      children,
      className: linkClassName,
      onClick,
      tabIndex,
    }: ProductLinkProps) =>
      href ? (
        <a
          aria-hidden={ariaHidden}
          href={href}
          className={linkClassName}
          onClick={onClick}
          tabIndex={tabIndex}
        >
          {children}
        </a>
      ) : (
        <>{children}</>
      );

    const link = renderLink ?? defaultRenderLink;

    const hasCardMenu = (cardMenuItems?.length ?? 0) > 0;

    const cardMenuSlotRef = useRef<HTMLDivElement>(null);
    /** Popover owns the element carrying the button role; find it by that. */
    const focusCardMenu = useCallback(
      () =>
        cardMenuSlotRef.current
          ?.querySelector<HTMLElement>('[role="button"]')
          ?.focus(),
      []
    );

    /**
     * The image action shares the tag row's band unless the CTA pushes it to
     * the left, so the tags have to stop short of it.
     */
    const hasTopRightImageAction =
      Boolean(renderImageAction) && ctaStyle !== 'image_top';

    const isImageCta = ctaStyle === 'image_top' || ctaStyle === 'image_bottom';
    const isInlineCta = ctaStyle === 'card_bottom';

    const ctaButton = (
      <CtaButton
        atMaxQuantity={atMaxQuantity}
        cardSize={cardSize}
        cartCount={cartCount}
        ctaIcon={ctaIcon}
        ctaStyle={ctaStyle}
        hasMultipleWeights={hasMultipleWeights}
        isUpdatingCart={isUpdatingCart}
        onAddToBag={onAddToBag}
        onDecrease={onDecrease}
        onIncrease={onIncrease}
        onSelectWeight={onSelectWeight}
        productName={name}
        renderAddToBag={renderAddToBag}
      />
    );

    return (
      <Flex
        ref={ref}
        flexDirection="column"
        height="100%"
        className={clsx(
          className,
          styles['productCard'],
          styles[`productCard_${cardSize}`],
          styles[`hover_${hover}`],
          {
            [styles['productCardPlacement']]: isPlacement,
            [styles['cardBorder']]: cardBorder,
            [styles[`cardBorder_${cardSize}`]]: cardBorder,
          }
        )}
        data-testid="product-card"
      >
        <Flex
          ref={impressionRef}
          position="relative"
          justifyContent="center"
          alignItems="center"
          width="100%"
        >
          {link({
            // Prevent link from being announced twice by assistive tech
            'aria-hidden': true,
            className: styles['imageLink'],
            onClick: onProductClick,
            tabIndex: -1,
            children: (
              <Card
                height="100%"
                width="100%"
                flat
                className={styles['productCardImage']}
              >
                <Flex
                  position="absolute"
                  flexDirection={
                    ctaStyle === 'image_top' ? 'column-reverse' : 'row'
                  }
                  flexWrap="wrap"
                  justifyContent="flex-start"
                  top={ctaStyle === 'image_top' ? 'auto' : 12}
                  bottom={ctaStyle === 'image_top' ? 12 : 'auto'}
                  left={12}
                  gap={8}
                  zIndex={1}
                  maxWidth={
                    hasTopRightImageAction
                      ? 'calc(100% - var(--card-image-action-clearance))'
                      : 'calc(100% - var(--card-overlay-inset) * 2)'
                  }
                >
                  {goldTagLabel && <JaneGoldTag label={goldTagLabel} />}
                  {specialLabel && <SpecialTag label={specialLabel} />}
                </Flex>
                <Card.Image
                  alt={imageAlt ?? name}
                  src={imageUrl ?? ''}
                  srcSet={imageSrcSet}
                  sizes={imageSizes}
                  loading={eagerLoad ? 'eager' : 'lazy'}
                />
              </Card>
            ),
          })}

          {renderImageAction && (
            <Flex
              position="absolute"
              top={12}
              {...(ctaStyle === 'image_top' ? { left: 12 } : { right: 12 })}
              zIndex={1}
            >
              {renderImageAction({ cardSize, focusCardMenu })}
            </Flex>
          )}

          {isImageCta && (
            <Flex
              position="absolute"
              top={ctaStyle === 'image_top' ? 12 : 'auto'}
              bottom={ctaStyle === 'image_bottom' ? 12 : 'auto'}
              right={12}
              zIndex={1}
            >
              {ctaButton}
            </Flex>
          )}
        </Flex>

        <Flex position="relative" flexDirection="column">
          {link({
            className: styles['productLink'],
            onClick: onProductClick,
            children: (
              <CardText
                brandInfo={brandInfo}
                dosageInfo={dosageInfo}
                hasMultipleWeightDiscount={hasMultipleWeightDiscount}
                isSponsored={isSponsored}
                name={name}
                potencyInfo={potencyInfo}
                ratingPlacement={hasCardMenu ? 'belowName' : 'firstLine'}
                ratingSummary={ratingSummary}
                renderProductCategoryLabel={renderProductCategoryLabel}
                px={isPlacement ? 8 : 0}
              />
            ),
          })}

          {hasCardMenu && (
            <div
              className={clsx(styles['cardMenuSlot'], {
                [styles['cardMenuSlot__placement']]: isPlacement,
              })}
              ref={cardMenuSlotRef}
            >
              <CardMenu items={cardMenuItems ?? []} productName={name} />
            </div>
          )}
        </Flex>

        <Flex
          className={styles['priceAndAddToCartContainer']}
          flexDirection="column"
          grow={2}
          shrink={1}
          gap={4}
          mt={2}
        >
          <Flex
            justifyContent={isInlineCta ? 'space-between' : undefined}
            alignItems="flex-end"
            mt="auto"
          >
            <Flex
              flexDirection={hasMultipleWeights ? 'column' : 'row'}
              alignItems={hasMultipleWeights ? 'baseline' : 'center'}
              alignSelf="center"
              flexWrap="wrap"
              mr={12}
            >
              {price && (
                <Typography
                  variant="body-semibold"
                  color={isDiscounted ? 'info' : 'text-main'}
                  mr={isDiscounted ? 4 : 0}
                >
                  {price}
                </Typography>
              )}
              {isDiscounted && originalPrice && !hideStrikethrough && (
                <Typography
                  color="grays-mid"
                  variant="small-body-regular"
                  strikeThrough
                >
                  {originalPrice}
                </Typography>
              )}
            </Flex>
            {isInlineCta && ctaButton}
          </Flex>

          {!isImageCta && !isInlineCta && ctaButton}
        </Flex>
      </Flex>
    );
  }
);
